/* eslint-disable import/no-cycle */

import { ApiClient } from '../api-client';
import { IPaginationParams } from '../types';

import {
  contactUsUrl,
  fetchAdminQuoteDetailsUrl,
  fetchAdminQuoteRequestsListUrl,
  fetchAllAdminRequestsUrl,
  fetchCompleteRegistrationUrl,
  fetchCountryListUrl,
  fetchEnterpriseMyRequestsListUrl,
  fetchEntMyRequestByIdUrl,
  fetchQuoteDetailsUrl,
  fetchRecentCustomerQuotes,
  fetchSignupListingUrl,
  postCreateAccountUrl,
  postSignUpUrl,
} from './urls';
import { formatAllAdminRequestsResponse } from './transformers';
import {
  IAllAdminRequestsListParams,
  IAllAdminRequestsResponse,
  IContactUsPayload,
  IContactUsResponse,
  ICountryListResponse,
  ICreateAccountResponse,
  IEntMyRequestDetailsParams,
  IEntMyRequestDetailsResponse,
  IEntMyRequestsListResponse,
  IEntQuoteDetailsResponse,
  IEntRecentCustomerQuotesListResponse,
  IQuoteRequestsListResponse,
  ISignUpListingResponse,
  ISignUpPayload,
  ISignUpResponse,
} from './types';

/**
 * This is provides the API client methods for all user authentication,
 * authorization and identity services like login, forgot password etc.
 *
 * NOTE: The `refreshToken` service has been moved out as a global to avoid
 * cyclic dependencies since its to be consumed within the base `ApiClient`.
 *
 */
export class B2BApiClient extends ApiClient {
  private static classInstance?: B2BApiClient;

  private constructor() {
    super({
      apiBaseURL: 'B2B_SERVICE_BASE_URL',
    });
  }

  /**
   * Applying the dreaded singleton pattern here to reuse the axios instance.
   */
  public static getClientInstance = () => {
    if (!this.classInstance) {
      this.classInstance = new B2BApiClient();
    }

    return this.classInstance;
  };

  /**
   * API call to fetch all the admin requests list
   */
  public fetchAllAdminRequestsList = async (
    queryParams: IAllAdminRequestsListParams
  ) => {
    // TODO: Remove this once the API is ready.
    // eslint-disable-next-line no-console
    console.info('🔥:fetchAllAdminRequestsList', { queryParams });

    const response = await this.get<IAllAdminRequestsResponse>(
      fetchAllAdminRequestsUrl()
    );

    if (!response.success) throw response;

    return formatAllAdminRequestsResponse(response.data.data);
  };

  /**
   * API to submit the b2b contact us form data.
   */
  public postContactUsForm = async (payload: IContactUsPayload) => {
    const response = await this.post<IContactUsResponse, IContactUsPayload>(
      contactUsUrl(),
      payload,
      {
        requiresAuth: false,
      }
    );

    if (!response.success) throw response;

    return response.data;
  };

  /**
   * API call to fetch all the enterprise requests list
   */
  public fetchEntMyRequestsList = async () => {
    const response = await this.get<IEntMyRequestsListResponse>(
      fetchEnterpriseMyRequestsListUrl(),
      {
        apiBaseURL: 'B2B_SERVICE_BASE_URL',
      }
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * API call to fetch all the enterprise request item details by their id.
   */
  public fetchEntMyRequestDetails = async ({
    id,
    activities = false,
  }: IEntMyRequestDetailsParams) => {
    const response = await this.get<IEntMyRequestDetailsResponse>(
      fetchEntMyRequestByIdUrl(id, activities),
      {
        apiBaseURL: 'B2B_SERVICE_BASE_URL',
      }
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * API call to post the data to sign up form.
   */
  public postSignUpForm = async (data: ISignUpPayload) => {
    const response = await this.post<ISignUpResponse, ISignUpPayload>(
      postSignUpUrl(),
      data
    );

    if (!response.success) throw response;

    return response.data;
  };

  /**
   * API call to post the data to create account form.
   */
  public postCreateAccountForm = async (data: ISignUpPayload) => {
    const response = await this.post<ICreateAccountResponse, ISignUpPayload>(
      postCreateAccountUrl(),
      data
    );

    if (!response.success) throw response;

    return response.data;
  };

  /**
   * API call to fetch all the sign-ups listing
   */
  public fetchSignupListing = async ({ limit, page }: IPaginationParams) => {
    const response = await this.get<ISignUpListingResponse>(
      fetchSignupListingUrl({ page, limit }),
      {
        apiBaseURL: 'B2B_SERVICE_BASE_URL',
      }
    );

    if (!response.success) throw response;

    return response.data;
  };

  /**
   * API call to fetch all the enterprise requests list
   */
  public fetchEntRecentCustomerQuotesList = async () => {
    const response = await this.get<IEntRecentCustomerQuotesListResponse>(
      fetchRecentCustomerQuotes(),
      {
        apiBaseURL: 'B2B_SERVICE_BASE_URL',
      }
    );
    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * API call to fetch all the enterprise requests list
   */
  public fetchQuoteRequestsList = async ({ page }: IPaginationParams) => {
    const response = await this.get<IQuoteRequestsListResponse>(
      fetchAdminQuoteRequestsListUrl(page),
      {
        apiBaseURL: 'B2B_SERVICE_BASE_URL',
      }
    );

    if (!response.success) throw response;

    return response.data;
  };

  /**
   * API call to fetch all the enterprise requests details
   */
  public fetchEntQuotesDetails = async (id: string, type: string) => {
    const response = await this.get<IEntQuoteDetailsResponse>(
      fetchQuoteDetailsUrl(id, type),
      {
        apiBaseURL: 'B2B_SERVICE_BASE_URL',
      }
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * API call to fetch all the admin requests details
   */
  public fetchAdminQuotesDetails = async (id: string, type: string) => {
    const response = await this.get<IEntQuoteDetailsResponse>(
      fetchAdminQuoteDetailsUrl(id, type),
      {
        apiBaseURL: 'B2B_SERVICE_BASE_URL',
      }
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * API call to fetch all the countries list
   */
  public fetchCountriesDetails = async () => {
    const response = await this.get<ICountryListResponse>(
      fetchCountryListUrl(),
      {
        apiBaseURL: 'B2B_SERVICE_BASE_URL',
      }
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * API call to fetch enterprise complete registration data.
   */
  public fetchEnterpriseDetails = async (token: string) => {
    const response = await this.get<ISignUpResponse>(
      fetchCompleteRegistrationUrl(token),
      {
        apiBaseURL: 'B2B_SERVICE_BASE_URL',
      }
    );

    if (!response.success) throw response;

    return response.data.data;
  };
}

/**
 * This creates a new instance of the class. is th base Axios API client Class
 * wrapper for All B2B Service related requests.
 */
export const B2B_SERVICE_API = B2BApiClient.getClientInstance();
